/**
 * The scoring-program builder: one program, on a full-window canvas.
 *
 * Opened from a sport's scoring page. That page also lists the workflows no
 * sport owns — Results — so a program here is either this sport's own, or one
 * with no sport at all, drawn in the same diagram.
 */

import { useParams } from "react-router-dom";

import { Problem } from "@/components/kit";
import { ProgramEditor } from "@/components/scoring/ProgramEditor";
import { getProgram } from "@/lib/scoring/api";
import { rolesFor, variantsFor } from "@/lib/scoring/lens";
import { useApi } from "@/spa/useApi";

export function ScoringBuilderPage() {
  const { sport = "", id = "" } = useParams<{ sport: string; id: string }>();

  const { data, loading, error } = useApi(
    async () => {
      const detail = await getProgram(id).catch(() => null);
      const owner = detail?.sportCode || null;
      if (!detail || (owner !== null && owner !== sport)) return { missing: true as const };
      const [variants, roles] = await Promise.all([variantsFor(owner), rolesFor()]);
      return { missing: false as const, detail, variants, roles };
    },
    [sport, id],
    "failed to load the program",
  );

  if (error) return <Problem>Could not load the program: {error}</Problem>;
  if (!data) return loading ? null : <Problem>Could not load the program.</Problem>;
  if (data.missing) return <Problem>No scoring program {id} in {sport}.</Problem>;

  return (
    <ProgramEditor
      initial={data.detail}
      variants={data.variants}
      roles={data.roles}
      back={{ href: `/${sport}/scoring`, label: "Scoring" }}
    />
  );
}
